import { type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router';

import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/Tooltip';

interface SidebarItemProps {
  icon: LucideIcon;
  label: string;
  to: string;
  collapsed: boolean;
  badge?: number;
}

export function SidebarItem({ icon: Icon, label, to, collapsed, badge }: SidebarItemProps) {
  const linkContent = (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          collapsed && 'justify-center px-2',
          isActive
            ? 'bg-sidebar-active text-white'
            : 'text-sidebar-foreground/80 hover:bg-sidebar-hover hover:text-white',
        )
      }
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{label}</span>
          {badge !== undefined && badge > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold text-white">
              {badge}
            </span>
          )}
        </>
      )}
      {collapsed && badge !== undefined && badge > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
          {badge}
        </span>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <div className="relative">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
          <TooltipContent side="right">
            {label}
            {badge !== undefined && badge > 0 && ` (${badge})`}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return linkContent;
}
